/* eslint-disable immutable/no-mutation */
import { useCallback, useState } from 'react';
import useForm from '~/client/hooks/form/useForm';
import useCurrentUser from '~/client/hooks/user/useCurrentUser';
import useSite from '~/client/hooks/useSite';
import useToastContext from '~/client/layout/DefaultLayout/LayoutContext/Toast/useToastContext';
import type FormValues from './FormValues';
import useSubmitSupportEmail from './useSubmitSupportEmail';
import useTopics from './useTopics';

const useContactSupport = ({ toggleDialog }: { toggleDialog: () => void }) => {
  const [files, setFiles] = useState<string[]>();
  const { showToast } = useToastContext();
  const { handleSubmitForm, loading } = useSubmitSupportEmail();
  const { profile } = useCurrentUser();
  const { site } = useSite();
  const helpTopics = useTopics();

  const topics: SelectOption[] = helpTopics
    .filter((topic) => topic.sites.includes(site))
    .map(({ value, label }) => ({
      value,
      label,
    }));

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles: string[] = [];
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const binaryStr = reader.result?.toString() ?? '';
          newFiles.push(binaryStr);
        };
        reader.readAsDataURL(file);
      });
      setFiles(newFiles);
    },
    [setFiles],
  );

  const { required, errors, getValues, handleSubmit, clearErrors, setErrors } =
    useForm<FormValues>({
      defaultValues: { email: profile?.email },
    });

  return {
    getValues,
    errors,
    required,
    onDrop,
    categories: topics,
    loading,
    handleSubmit: handleSubmit(async (values) => {
      clearErrors();
      const response = await handleSubmitForm(values, files);
      if (response?.errors) {
        setErrors(response?.errors as GenericErrorType[]);
        return;
      }
      showToast({
        message: 'general.success',
        variant: 'success',
        position: 'bottom',
        timeout: 5000,
      });
      toggleDialog();
    }),
  };
};

export default useContactSupport;
